import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  ColumnDef,
  getSortedRowModel,
  SortingState,
  OnChangeFn,
} from '@tanstack/react-table';
import { compact } from 'lodash';
import { HTMLProps, useEffect, useMemo, useRef } from 'react';
import { cn } from '../../../utils/helpers';

type SortingProps =
  | {
      clientSideSorting: true;
      serverSideSorting?: false;
    }
  | {
      clientSideSorting?: false;
      serverSideSorting: true;
      sorting?: SortingState;
      setSorting?: OnChangeFn<SortingState>;
    };

type PROPS<TData> = SortingProps & {
  data: TData[];
  columns: ColumnDef<TData>[];
  withCheckbox?: boolean;
  sorting?: SortingState;
  setSorting?: OnChangeFn<SortingState>;
};

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<any>(null!);

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);

  return <div ref={ref}>checkbox</div>;
}

const Table = <TData extends object>({
  withCheckbox = false,
  data,
  columns: _columns,
  clientSideSorting,
  serverSideSorting,
  sorting,
  setSorting,
}: PROPS<TData>) => {
  const memoizedData = useMemo(() => data, [data]);
  const memoizedColumns = useMemo(() => _columns, [_columns]);

  const columns = useMemo<ColumnDef<TData>[]>(() => {
    return compact([
      withCheckbox && {
        id: 'select',
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      ...memoizedColumns,
    ]);
  }, [memoizedColumns, withCheckbox]);

  const table = useReactTable({
    data: memoizedData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    ...(clientSideSorting && { getSortedRowModel: getSortedRowModel() }),
    ...(serverSideSorting && { manualSorting: true }),
  });

  return (
    <div className="border border-gray rounded-lg w-full">
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <th
                  className={cn(
                    'border border-gray p-4 border-t-0',
                    index === 0 && 'w-[430px]',
                    index + 1 === headerGroup.headers.length && 'border-r-0',
                    index === 0 && 'border-l-0',
                  )}
                  key={header.id}
                >
                  {!header.isPlaceholder && (
                    <div
                      onClick={header.column.getToggleSortingHandler()}
                      className={cn('flex items-center', header.column.getCanSort() && 'cursor-pointer select-none')}
                    >
                      <div className={cn('flex-1', 'text-left font-medium', 'flex items-center')}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell, index, array) => (
                <td
                  className={cn(
                    'w-fit max-w-[430px]',
                    'border border-gray',
                    index + 1 === array.length && 'border-r-0',
                    index === 0 && 'border-l-0',
                    row.index + 1 === table.getRowModel().rows.length && 'border-b-0',
                  )}
                  key={cell.id}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
